/**
 * OUTGOING MESSAGE API — Wrapper REST Liferay allineato a OutgoingMessagesResourceImpl.java.
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET   /outgoing-messages/{groupId}  pagination server-side          -> Page<OutgoingMessage>
 *     Query params: page, pageSize, sort, search, status (int dell'enum, assente = tutti).
 *   - GET   /outgoing-message/{id}                                       -> OutgoingMessage
 *   - PATCH /outgoing-message/{id}         OutgoingMessageInput          -> OutgoingMessage
 *   - POST  /outgoing-message/{id}/send                                  -> OutgoingMessage (SENT o FAILED)
 *   - POST  /outgoing-message/{id}/cancel                                -> OutgoingMessage (CANCELLED)
 *
 * Le righe le crea solo il backend, quando una mail parte: niente create/delete.
 * NB: singolare per get/update/azioni, plurale per list.
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import {
  outgoingMessageFromDto,
  outgoingMessageStatusToInt,
  type OutgoingMessageDto,
} from './outgoingMessageMappers';
import type {
  OutgoingMessage,
  OutgoingMessageStatus,
  OutgoingMessageUpdatePayload,
} from './outgoingMessageTypes';

const COLLECTION = '/outgoing-messages';
const SINGLE = '/outgoing-message';

export interface OutgoingMessageListParams extends ServerListParams {
  /** Filtro per stato; omesso = tutti. */
  status?: OutgoingMessageStatus;
}

export const outgoingMessageApi = {
  getAll: (groupId: number, params?: OutgoingMessageListParams): Promise<Page<OutgoingMessage>> => {
    const { status, ...query } = params ?? {};
    const queryParams: Record<string, unknown> = { ...query };
    if (status !== undefined) queryParams.status = outgoingMessageStatusToInt(status);
    return httpClient
      .get<Page<OutgoingMessageDto>>(`${COLLECTION}/${groupId}`, { params: queryParams })
      .then((r) => ({ ...r.data, items: r.data.items.map(outgoingMessageFromDto) }));
  },

  getById: (outgoingMessageId: number): Promise<OutgoingMessage> =>
    httpClient
      .get<OutgoingMessageDto>(`${SINGLE}/${outgoingMessageId}`)
      .then((r) => outgoingMessageFromDto(r.data)),

  update: (outgoingMessageId: number, payload: OutgoingMessageUpdatePayload): Promise<OutgoingMessage> =>
    httpClient
      .patch<OutgoingMessageDto>(`${SINGLE}/${outgoingMessageId}`, payload)
      .then((r) => outgoingMessageFromDto(r.data)),

  send: (outgoingMessageId: number): Promise<OutgoingMessage> =>
    httpClient
      .post<OutgoingMessageDto>(`${SINGLE}/${outgoingMessageId}/send`)
      .then((r) => outgoingMessageFromDto(r.data)),

  cancel: (outgoingMessageId: number): Promise<OutgoingMessage> =>
    httpClient
      .post<OutgoingMessageDto>(`${SINGLE}/${outgoingMessageId}/cancel`)
      .then((r) => outgoingMessageFromDto(r.data)),
};
